/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2019] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

var Backbone = require('backbone');
var Marionette = require('backbone.marionette');
var Radio = require('backbone.radio');

var UIUtil = require('psa-core/util/ui');
var userHasRole = require('common/util/userHasRole');

var SideNavSectionView = require('common/views/SideNavSectionView');
var BreadcrumbView = require('common/views/BreadcrumbView');
var DropdownView = require('common/views/DropdownView');
var ConfirmationModalView = require('common/views/ConfirmationModalView');
var ContentServerLinkView = require('common/views/ContentServerLinkView');

var SubmitRecord = require('../../common/services/SubmitRecord');
var CorpFinRecordHeaderBarView = require('../../common/views/CorpFinRecordHeaderBarView');
var CorpFinExemExemptionSectionView = require('./CorpFinExemExemptionSectionView');
var CorpFinExemSecuritiesDescriptionSectionView = require('./CorpFinExemSecuritiesDescriptionSectionView');

var tmplCorpFinExemptionDashboardView = require('../templates/CorpFinExemptionDashboardView.hbs');

module.exports = Marionette.LayoutView.extend({
  template: tmplCorpFinExemptionDashboardView,

  className: 'corp-fin-exemption-dashboard-view container-fluid m-b-lg',

  ui: {
    exemptionWrapper: '.exemption-wrapper',
    csLinkContainer: '.cs-link-container',
    exemptionActionDropDown: '.dropdown-container',
    headerBar: '.header-bar',
    breadcrumb: '.breadcrumb-container'
  },

  regions: {
    exemptionWrapper: '@ui.exemptionWrapper',
    csLinkContainer: '@ui.csLinkContainer',
    exemptionActionDropDown: '@ui.exemptionActionDropDown',
    headerBar: '@ui.headerBar',
    breadcrumb: '@ui.breadcrumb'
  },

  onBeforeShow: function() {
    var recordId = this.model.get('recordId');
    var record = this.model.get('record') || {};
    var recordCategoryId = record.recordCategoryId;
    var fileNumber = record.fileNumber;
    var isReadOnly = userHasRole('CF - Read Only');
    var isEfdRecord = record.isEfdRecord === '1';

    this.showChildView(
      'breadcrumb',
      new BreadcrumbView({
        breadcrumbs: [
          {
            link: '#corpfin/dashboard',
            text: 'Home'
          },
          {
            text: 'Exemption (' + fileNumber + ')'
          }
        ]
      })
    );

    if (!isReadOnly) {
      var dropDownOptions = [
        {
          text: 'Edit Exemption',
          link: '#corpfin/exemption/' + recordId + '/edit-details'
        },

        {
          isDivider: true
        },
        {
          text: 'Add Fee',
          link: '#corpfin/exemption/' + recordId + '/new-fee'
        },
        {
          text: 'Edit Securities Description',
          link: '#corpfin/exemption/' + recordId + '/edit-securities-description'
        },
        {
          text: 'Add Secondary Name',
          link: '#corpfin/exemption/' + recordId + '/new-secondary-name'
        },
        {
          text: 'Add Sales Compensation',
          link: '#corpfin/exemption/' + recordId + '/new-sales-comp'
        },
        {
          text: 'Delete Exemption',
          onClick: this.onClickDelete.bind(this)
        }
      ];
    }

    this.showChildView(
      'exemptionActionDropDown',
      new DropdownView({
        rightAlign: true,
        model: new Backbone.Model({
          name: 'Exemption Actions',
          options: dropDownOptions
        })
      })
    );

    if (record.recordCSFolderId || !isEfdRecord) {
      this.showChildView(
        'csLinkContainer',
        new ContentServerLinkView({
          model: new Backbone.Model({
            buttonText: 'Open Exemption Folder',
            contentServerId: record.recordCSFolderId
          })
        })
      );
    }

    this.showChildView(
      'headerBar',
      new CorpFinRecordHeaderBarView({
        model: new Backbone.Model(record)
      })
    );

    this.exemption = new SideNavSectionView({
      collection: new Backbone.Collection([
        {
          id: 'exemption',
          title: 'Exemption',
          view: new CorpFinExemExemptionSectionView({
            model: new Backbone.Model(record)
          })
        },
        {
          id: 'fee',
          title: 'Fee',
          view: Radio.channel('cf').request('build:corp-fin-fees-section-view', recordId, recordCategoryId, isReadOnly)
        },
        {
          id: 'priorNames',
          title: 'Prior Names',
          view: Radio.channel('cf').request(
            'build:corp-fin-prior-names-section-view',
            recordId,
            recordCategoryId,
            isReadOnly
          )
        },
        {
          id: 'securitiesDescription',
          title: 'Securities Description',
          view: new CorpFinExemSecuritiesDescriptionSectionView({
            model: new Backbone.Model(record)
          })
        },
        {
          id: 'secondaryNames',
          title: 'Secondary Names',
          view: Radio.channel('cf').request(
            'build:corp-fin-secondary-names-section-view',
            recordId,
            recordCategoryId,
            isReadOnly
          )
        },
        {
          id: 'salesCompensation',
          title: 'Sales Compensation',
          view: Radio.channel('cf').request('build:corp-fin-sales-compensation-section-view', recordId, isReadOnly)
        },
        {
          id: 'notes',
          title: 'Notes',
          view: Radio.channel('cf').request('build:notes-view', {
            objectId: recordId,
            objectTypeId: 'EXEMPTION'
          })
        },
        {
          id: 'auditLog',
          title: 'Audit Log',
          view: Radio.channel('cf').request('build:audit-log-view', {
            objectId: recordId,
            objectTypeId: 'EXEMPTION'
          })
        }
      ])
    });
    this.showChildView('exemptionWrapper', this.exemption);
  },

  onClickDelete: function() {
    var self = this;
    var message = 'Are you sure you want to delete this exemption?';
    UIUtil.showModal({
      title: 'Confirm Deleting Exemption',
      view: new ConfirmationModalView({
        confirmText: 'Delete Exemption',
        model: new Backbone.Model({
          message: message,
          onConfirm: function() {
            var recordId = self.model.get('recordId');
            return SubmitRecord({ recordId: recordId, isDeleted: '1' });
          },
          onConfirmDone: function() {
            Radio.channel('navigate').trigger('show:int:cf:dashboard');
          }
        })
      })
    });
  }
});
